import getTime from 'date-fns/getTime'
import addMinutes from 'date-fns/addMinutes'
import format from 'date-fns/format'
import { logger } from '@/helpers/helpers'

const domainCachePlugin = store => {
  const CACHE_TIME = 60

  /**
   * Set current date in milliseconds
   * @returns {number} Time in milliseconds
   */
  const setCurrentDate = () => {
    const date = getTime(new Date())
    return parseInt(format(date, 'x'))
  }

  /**
   * Set expire date in milliseconds (Current type plus 10 minutes)
   * @returns {number} Time in milliseconds
   */
  const setExpireDate = (response) => {
    const date = getTime(new Date())
    response['cache_expire'] = parseInt(format(addMinutes(date, CACHE_TIME), 'x'))

    return response
  }

  /**
   * Check check cache for expiration
   * @param {object} response - Time in milliseconds
   * @returns {boolean} - true if expired
   */
  const isExpired = (response) => {
    if (response.cache_expire) {
      return (response.cache_expire - setCurrentDate()) <= 0
    } else {
      return true
    }
  }

  /**
   * Check if cache valid or not
   * @param actionName {string}
   * @returns {boolean}
   */
  const cacheKeys = actionName => {
    let existing = localStorage.getItem('cache_keys')

    existing = existing ? existing.split(',') : []

    if (existing.indexOf(actionName) === -1) {
      existing.push(actionName)
      localStorage.setItem('cache_keys', existing.toString())
    }
  }

  /**
   * Wrapper around API calls
   * @param {string} actionName - Endpoint relative path
   * @param {object} payload - Data that appends to request body
   * @returns {Promise<*>}
   */
  const request = async (actionName, payload) => {
    const data = {
      method: actionName,
      data: {
        domain_id: payload.domain_id
      }
    }
    // Call Vuex dispatch 'makecall'
    let response
    try {
      response = await store.dispatch('makecall', data)
    } catch (e) {
      // Cache domain error
      response = {
        result: 'error'
      }
    }
    // Add domain_id to response
    response['domain_id'] = payload.domain_id

    // Add expiry date to response
    const finalResponse = setExpireDate(response)

    // Select present data
    const oldItems = JSON.parse(localStorage.getItem('domains_list')) || []

    // Add a new item to data
    oldItems.push(finalResponse)

    // Write data to storage
    localStorage.setItem('domains_list', JSON.stringify(oldItems))

    // Return response
    return finalResponse
  }

  /**
   * Caching function
   * @param {string} actionName - Endpoint relative path
   * @param {object} payload - Data that appends to request body
   * @param {boolean} refresh - Flag to forced data refresh
   * @param {boolean} request - Flag to forced data refresh
   * @returns {Promise<Object>}
   */
  const cache = async (actionName = '', payload = {}, refresh = false) => {
    cacheKeys('domains_list')
    // If no entry, create a new one
    const storageItem = JSON.parse(localStorage.getItem('domains_list')) || []

    // Check if domain_details already has object with id from payload
    let obj = storageItem.find(obj => obj.domain_id === payload.domain_id)
    // If find, check if cache is not expired
    if (obj) {
      logger(`[${obj.domain_id}] - was found in cache`)
      // If cache expired or refresh flag is set to true
      if (isExpired(obj) || refresh) {
        logger(`[${obj.domain_id}] - cache expired. Remove entry from storage and get a fresh data`)

        // Find index of required object in array and remove it
        let removeIndex = storageItem.map(item => item.domain_id).indexOf(obj.domain_id)
        storageItem.splice(removeIndex, 1)
        localStorage.setItem('domains_list', JSON.stringify(storageItem))

        return request(actionName, payload)
      } else {
        // If cache is not expired, return selected object
        logger(`[${obj.domain_id}] - cache is not expired`)
        return obj
        // return {}
      }
    } else {
      // If no data, get a data from response
      logger(`[${payload.domain_id}] - data not found. Get a new data`)
      return request(actionName, payload)
    }
  }

  /**
   * Update cached value
   * @param {object} domain - New domain data to be stored
   */
  const updateValue = (domain) => {
    cacheKeys('domains_list')
    // If no entry, create a new one
    const storageItem = JSON.parse(localStorage.getItem('domains_list')) || []
    // Check if domain_details already has object with id from payload
    const storedDomain = storageItem.find(obj => obj.domain_id === domain.domain_id)
    if (!storedDomain) {
      return
    }
    const replaceIndex = storageItem.map(item => item.domain_id).indexOf(storedDomain.domain_id)
    storageItem.splice(replaceIndex, 1, domain)
    localStorage.setItem('domains_list', JSON.stringify(storageItem))
    logger(`[${storedDomain.domain_id}] - localStorage updated`)
  }

  store.subscribe((mutation) => {
    // subscribe for POPULATE_DOMAIN to update localStore data if payload contains updateCachedValue
    if (mutation.type === 'POPULATE_DOMAIN' && mutation.payload?.updateCachedValue) {
      updateValue(mutation.payload)
    }
  })

  store.domaincache = cache
}

export default domainCachePlugin
